import { useLingui } from "@lingui/react/macro";
import { Check, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import type { DataModel } from "../../../../models/data-model";
import type { Column, EditMode } from "./types";

import { Popover, PopoverAnchor, PopoverContent } from "../../base/popover";
import { Tooltip } from "../../base/tooltip";
import { Button } from "../../button";
import { Cell } from "../cells/cell";
import { CopyCellButton } from "./copy-cell-button";
import { useRowIndex } from "./row-index-context";

export const EditableCell = observer(function EditableCell<T extends object>({
  item,
  column,
  model,
  editMode,
  multiEdit,
  onEdit,
}: {
  item: T;
  column: Column<T>;
  model: DataModel<T>;
  editMode: EditMode;
  multiEdit?: boolean;
  onEdit?: (patch: Partial<T>, row: T) => void | Promise<unknown>;
}) {
  const { t } = useLingui();
  const index = useRowIndex();
  const id = model.idAttr != null ? item[model.idAttr] : undefined;
  const multiDraft = multiEdit ? model.multiEditDrafts.get(id) : undefined;
  // The draft's keys ARE the editable fields for this row (built in
  // startMultiEditing). Gating on membership here avoids turning non-editable
  // columns into inputs and naturally supports rows whose editable set differs.
  const multiEditing =
    !!multiDraft && column.attr != null && column.attr in multiDraft;
  // Read editingItemId unconditionally so this observer subscribes to it in
  // every edit mode. Otherwise a custom cell that reads editing state (e.g.
  // the medications "Change dosage" inline editors, in dialog mode) wouldn't
  // re-render when editing starts, since the short-circuit below would skip
  // the read.
  const isEditingThisRow = model.editingItemId === id;
  // Dialog mode leaves `editingItemId` unset for its own editing, so a row
  // inline-edited here was started by an external flow (the medications
  // "Change dosage" button). Such a flow scopes the edit to a field set, which
  // `canEditField` below honours — so only that flow's fields become inputs.
  // `isEditable` on top of that keeps `Column.editable` authoritative: a
  // model-supplied field set is hand-written, so it can name a column the
  // column itself refuses to edit for this row.
  const singleEditing =
    !multiEdit &&
    isEditingThisRow &&
    column.attr != null &&
    model.canEditField(column.attr) &&
    isEditable(column, item);
  const editing = multiEditing || singleEditing;
  const draft = multiEditing
    ? multiDraft
    : singleEditing
      ? (model.editingDraft as Partial<T>)
      : undefined;

  // Static display of multiline columns clamps to a fixed line count so every
  // row keeps the same height. Renders the raw value instead of going through
  // the shared Cell — its nested flex wrappers break -webkit-line-clamp, and
  // its multiline mode is the collapse/expand UI which varies row heights.
  if (
    !editing &&
    column.type === "string" &&
    column.multiline &&
    !column.cell &&
    column.attr != null
  ) {
    const lines = column.multiline === true ? 2 : column.multiline;
    return (
      <MultilineClampCell
        value={String(item[column.attr] ?? "")}
        lines={lines}
      />
    );
  }

  const cell = (
    <Cell
      item={draft ? ({ ...item, ...draft } as T) : item}
      patch={draft as T | undefined}
      column={column}
      index={() => index}
    />
  );

  if (editMode === "cell" && singleEditing) {
    const save = async () => {
      // Mirror row-mode submit (data-table.tsx): send only changed fields,
      // and skip the callback entirely when nothing changed.
      const patch = model.generatePatch();
      if (patch && Object.keys(patch).length > 0) {
        await onEdit?.(patch as Partial<T>, item);
      }
      model.stopEditing();
    };
    // The editor floats in a popover so it isn't bound by the (fixed) column
    // width — a narrow column has no room for an input plus two buttons. The
    // anchor keeps showing the read-only value in the cell. The popover content
    // is portaled out of the table's <Form>, so Enter no longer submits it;
    // restore Enter-to-save here (but not for textareas, where Enter is a
    // newline). Escape-to-cancel comes from Radix via onOpenChange.
    return (
      <Popover open onOpenChange={(open) => !open && model.stopEditing()}>
        <PopoverAnchor>
          <Cell item={item} column={column} index={() => index} />
        </PopoverAnchor>
        <PopoverContent
          side="bottom"
          align="center"
          className="flex w-auto items-center gap-1 p-1.5"
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !(event.target instanceof HTMLTextAreaElement)
            ) {
              event.preventDefault();
              void save();
            }
          }}
        >
          {cell}
          <Button
            variant="subtle"
            size="sm"
            aria-label={t`Save`}
            onClick={async (event) => {
              event.stopPropagation();
              await save();
            }}
          >
            <Check className="text-success-foreground" size={12} />
          </Button>
          <Button
            variant="subtle"
            size="sm"
            aria-label={t`Cancel`}
            onClick={(event) => {
              event.stopPropagation();
              model.stopEditing();
            }}
          >
            <X className="text-danger-foreground" size={12} />
          </Button>
        </PopoverContent>
      </Popover>
    );
  }

  // Array columns render their own per-tag copy icon (CellTags), so they skip
  // the single cell-level copy-all button here to avoid a double affordance.
  if (
    !editing &&
    column.copyable &&
    column.type !== "array" &&
    column.attr != null
  ) {
    const copyValue = String(item[column.attr] ?? "");
    if (copyValue) {
      return (
        <span className="inline-flex items-center gap-1">
          {cell}
          <CopyCellButton value={copyValue} />
        </span>
      );
    }
  }

  return cell;
});

// Clamped multiline value with a hover tooltip that reveals the full text, but
// only when the value is actually truncated (overflow-detected, mirroring the
// single-line case in cell-text-static). The fixed line clamp keeps every row
// the same height, so the tooltip is the only way to read the overflowing text.
function MultilineClampCell({
  value,
  lines,
}: {
  value: string;
  lines: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, lines]);

  return (
    <Tooltip
      content={overflows ? value : undefined}
      className="max-w-xs text-wrap"
    >
      {/* Outer flex box reserves the clamp height and vertically centers the
          text, so short (single-line) values sit in the middle of the taller
          multiline cell rather than pinned to its top. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: `${lines}lh`,
        }}
      >
        <div
          ref={ref}
          style={{
            flex: 1,
            minWidth: 0,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: lines,
            overflow: "hidden",
          }}
        >
          {value}
        </div>
      </div>
    </Tooltip>
  );
}

export function isEditable<T>(column: Column<T>, item: T): boolean {
  return typeof column.editable === "function"
    ? column.editable(item)
    : !!column.editable;
}

/**
 * The `attr`s of the columns editable for `item`. Pass the real row when
 * starting an edit; pass an empty object (`{} as T`) when computing the set for
 * a not-yet-created add row.
 */
export function editableFields<T>(columns: Column<T>[], item: T): (keyof T)[] {
  return columns.flatMap((c) =>
    isEditable(c, item) && c.attr != null ? [c.attr] : [],
  );
}
